import React from 'react';
import { ErrorBoundary } from '../components/ErrorBoundary';
import { ToastContainer } from '../components/ui/ToastContainer';
import { VisibilityTracker } from '../components/app/VisibilityTracker';
import { SidebarProvider } from '../contexts/SidebarContext';
import { TopicListProvider } from '../contexts/TopicListContext';
import { SidebarShortcutHandler } from '../components/app/SidebarShortcutHandler';
import { ProjectSwitchHandler } from '../components/app/ProjectSwitchHandler';
import { CapacitorContainer } from '../components/mobile/CapacitorContainer';
import { useViewport } from '../contexts/ViewportContext';

interface AppLayoutProps {
  children: React.ReactNode;
}

export function AppLayout({ children }: AppLayoutProps) {
  const { isMobile } = useViewport();

  // Apply the background color to the entire app only on desktop
  // This allows us to keep the mobile view unchanged
  const appBackgroundClass = isMobile ? '' : 'bg-[var(--app-bg-color)]';

  return (
    <ErrorBoundary>
      {/* Track visibility changes to suppress loading indicators during tab switches */}
      <VisibilityTracker />

      {/* Toast container - renders at the app level */}
      <ToastContainer />

      {/* Capacitor container for safe area handling in mobile apps */}
      <CapacitorContainer>
        {/* Sidebar context provider for sidebar state management */}
        <SidebarProvider>
          {/* Topic list context provider for topic list state management */}
          <TopicListProvider>
            {/* Initialize keyboard shortcuts */}
            <SidebarShortcutHandler />
            <ProjectSwitchHandler />
            {/* Apply background color to the app container on desktop only */}
            <div className={`h-screen ${appBackgroundClass}`}>{children}</div>
          </TopicListProvider>
        </SidebarProvider>
      </CapacitorContainer>
    </ErrorBoundary>
  );
}

export default AppLayout;
